<ng-template #twoFAHelp let-modal>
  <div class="modal-header">
	<h5 class="modal-title d-flex align-items-center">
	  <mat-icon class="me-2">help_outline</mat-icon>
	  How to Set Up 2FA
	</h5>
	<button mat-icon-button (click)="modal.dismiss()" aria-label="Close">
	  <mat-icon>close</mat-icon>
	</button>
  </div>

  <div class="modal-body">
	<p><strong>Step 1:</strong> Open your authenticator app (Google, Microsoft, etc.)</p>
	<p><strong>Step 2:</strong> Tap <em>Add Account</em> and scan the QR code.</p>
	<p><strong>Step 3:</strong> Enter the 6-digit code shown in your app.</p>
	<p><strong>Step 4:</strong> Once verified, the device is remembered unless browser storage is cleared.</p>
  </div>

  <div class="modal-footer">
	<button mat-stroked-button color="primary" (click)="modal.close()">Close</button>
  </div>
</ng-template>
<div class="auth-wrapper">

  <div class="auth-box" [ngClass]="{ 'auth-box-wide': awaiting2FA }">
	<div class="card">
	  <div class="card-body text-center">
		<!-- Logo -->
		
		<img class="auth-logo img img-fluid mb-3" [src]="logo" alt="System Logo"  *ngIf="!awaiting2FA"/>

		<!-- Title -->
		<h3 class="auth-title" *ngIf="!awaiting2FA">
		  <i class="icon-user mr-2"></i> {{ systemType || 'Login' }}
		</h3>

		<!-- Simplified Inline Device Info -->
		
		<div *ngIf="awaiting2FA && deviceInfo?.info" class="mb-4 px-2">
		  <div class="row">
			<!-- QR Code + Help -->
			<div class="col-12 col-md-6 text-center mb-4 mb-md-0" *ngIf="!userHasDeviceRegistered">
			  <p class="mb-3"><b>Scan this code in your authenticator app:</b></p>
			  <img *ngIf="qrCodeDataUrl" [src]="qrCodeDataUrl" class="img-fluid mb-3" alt="QR Code" />
			  <div class="text-center">
				<button mat-button color="primary" (click)="openHelpModal()" class="help-button">
				  <mat-icon class="me-1">help</mat-icon>
				  <span class="d-none d-sm-inline">Need help setting this up?</span>
				  <span class="d-sm-none">Help</span>
				</button>
			  </div>
			</div>
		
			<!-- Device Info -->
			<div class="col-12 col-md-6 text-left small text-muted">
			  <div class="mb-2" *ngIf="deviceInfo.info.platform">
				<mat-icon class="me-1 mt-1">computer</mat-icon>
				&nbsp;&nbsp;<strong>Platform:</strong> {{ deviceInfo.info.platform }}
			  </div>
		
			  <div class="mb-2" *ngIf="deviceInfo.info.timezone">
				<mat-icon class="me-1 mt-1">schedule</mat-icon>
				&nbsp;&nbsp;<strong>Timezone:</strong> {{ deviceInfo.info.timezone }}
				<span *ngIf="deviceInfo.info.timezoneOffset">
				  ({{ deviceInfo.info.timezoneOffset }} mins)
				</span>
			  </div>
		
			  <div class="mb-2" *ngIf="deviceInfo.info.userAgent">
				<mat-icon class="me-1 mt-1">language</mat-icon>
				&nbsp;&nbsp;<strong>User Agent:</strong>
				<div class="text-break small">{{ deviceInfo.info.userAgent }}</div>
			  </div>
			</div>
		  </div>
		</div>
		
		

		<!-- Login / 2FA Form -->
		<form
		  [formGroup]="loginForm"
		  (ngSubmit)="awaiting2FA ? onTwoFactorSubmit() : onLogin()"
		  class="auth-form"
		  novalidate
		>
		  <!-- Username -->
		  <div class="form-group" *ngIf="!awaiting2FA">
			<label for="username">Username</label>
			<input
			  type="email"
			  id="username"
			  formControlName="userName"
			  class="form-control"
			  placeholder="Enter your username"
			  [disabled]="isLoggingIn"
			  required
			/>
		  </div>

		  <!-- Password -->
		  <div class="form-group" *ngIf="!awaiting2FA">
			<label for="password">Password</label>
			<input
			  type="password"
			  id="password"
			  formControlName="password"
			  class="form-control"
			  placeholder="Enter your password"
			  (keydown)="onPasswordKeyDown($event)"
			  [disabled]="isLoggingIn"
			  required
			/>
		  </div>

		  <!-- 2FA Input -->
		  <!-- Modern 2FA Code Input -->
		 <div class="form-group text-center" *ngIf="awaiting2FA">
			 <label for="twoFactorCode" class="d-block mb-3">Enter 2FA Code</label>
			 <div class="d-flex justify-content-center flex-wrap gap-1 gap-sm-2">
				<input
					*ngFor="let i of [0,1,2,3,4,5]"
					maxlength="1"
					class="otp-box"
					type="text"
					inputmode="numeric"
					pattern="[0-9]*"
					autocomplete="one-time-code"
					(input)="onOtpInput($event, i)"
					(keydown)="onOtpKeyDown($event, i)"
					(paste)="handleOtpPaste($event, i)"
					[id]="'otp-' + i"
					[attr.aria-label]="'Digit ' + (i + 1) + ' of 6'"
				/>
			 </div>
			 <small class="text-muted mt-2 d-block">Enter the 6-digit code from your authenticator app</small>
		 </div>

		  <!-- Error Message -->
		  <div *ngIf="loginForm.value.txterror || txterror" class="alert alert-danger mt-3" role="alert">
			<div class="d-flex align-items-center">
			  <i class="icon-warning me-2"></i>
			  <div>
				<strong>Login Error:</strong>
				<div class="mt-1">{{ loginForm.value.txterror || txterror }}</div>
			  </div>
			</div>
		  </div>

		  <!-- Submit Button -->
		  <div class="form-button mt-4">
			<button 
			  class="btn btn-primary btn-block" 
			  type="submit"
			  [disabled]="isLoggingIn"
			>
			  <span *ngIf="!isLoggingIn">
				{{ awaiting2FA ? 'Verify 2FA' : (systemType ? 'Access ' + systemType : 'Login') }}
			  </span>
			  <span *ngIf="isLoggingIn" class="d-flex align-items-center justify-content-center">
				<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
				Logging in as {{ loginForm.value.userName }}...
			  </span>
			</button>
			
			<!-- Login Status Info 
			<div *ngIf="isLoggingIn" class="login-status mt-2 text-center">
			  <small class="text-muted">
				<i class="icon-globe me-1"></i>
				Connecting to: {{ loginEndpoint }}
			  </small>
			</div>
			-->
		  </div>
		</form>
	  </div>
	  
	  
	</div>
  </div>
</div>